import React, { useEffect, useReducer } from 'react';
import { useParams } from 'react-router';
import { useAppActions, useAppState } from '../../../../overmind';
import { FetchStatus, FetchType } from '../../../../enums';
import { PagePath } from '../../../../navigation/navigation.enums';
import { EmptyState } from '../../../../components/general/empty-state/empty-state';
import * as store from './agent.store';
import { Button, PageHeader, PillButtonGroup, SearchBar, Table, TableColumn, TableRow, TextArea, TextField, Modal, Tabs, Tab, CascadingPanels, CascadingPanel, useCascadingPanelsConnector, PanelHeader } from '@keplerco/core';
import { TertiaryButton } from '../../../../components/general/tertiary-button/tertiary-button';
import { ConfirmationModalLayout } from '../../../../widgets/layouts';
import { TestAgentPanel } from '../test-agent/test-agent.panel';
import { PageLoaderLayer } from '../../../../components/general/loading-state/loader-layers/page-loader-layer/page-loader-layer';
import { WidgetCard } from '../../../analytics/analytics-filters/widget-card';
import { JsonViewer } from '../../../../components/json-viewer/json-viewer';
import { useForm } from 'react-hook-form';

enum CascadingPanelIds {
  Request = 'Request',
  Response = 'Response',
}

enum PillButtonDescriptions {
  SearchContexts = 'Search Contexts',
  SelectedContexts = 'Selected Contexts',
}

export function AgentPage(): JSX.Element {
  const { agentTypeName } = useParams();

  const actions = useAppActions();
  const { fetchState, companyVariables } = useAppState();

  const [state, dispatch] = useReducer(store.reducer, store.initialState);
  const { handleSubmit } = useForm();
  const { openPanelIds, next, previous } = useCascadingPanelsConnector();

  useEffect(() => {
    async function getData() {
      if (!companyVariables.slug || !agentTypeName) return;

      actions.startLoader({ path: PagePath.aiManagementAgent, type: FetchType.PageFetching });

      const availableContexts = await actions.getContextsByCompany();
      dispatch({ type: store.AgentPageActionTypes.SetAvailableContexts, payload: availableContexts });

      const agent = await actions.getAgent({ companySlug: companyVariables.slug, agentTypeName });
      dispatch({ type: store.AgentPageActionTypes.SetAgent, payload: agent });

      actions.stopLoader(PagePath.aiManagementAgent);
    }

    getData();
  }, [companyVariables.slug, agentTypeName]);

  return (
    <PageLoaderLayer path={PagePath.aiManagementAgent}>
      <div className="wrapper stack" style={{ gap: 24 }}>
        {!!state.agent ? (
          <PageHeader
            breadcrumbs={[
              { name: 'AI management', url: `${PagePath.aiManagementBase}` },
              { name: 'Agents', url: `${PagePath.aiManagementBase}${PagePath.aiManagementAgents.replace(':companySlug', companyVariables.slug!)}` },
              { name: state.agent.displayName ?? state.agent.agentTypeName, url: location.pathname },
            ]}
            title={state.agent.displayName ?? state.agent.agentTypeName}
            subtitle={state.agent.description}
            type="actions"
          >
            <div className="headerActions">
              <Button type="button" onClick={() => next(CascadingPanelIds.Request)}>
                Try it out
              </Button>
            </div>
          </PageHeader>
        ) : (
          <PageHeader
            breadcrumbs={[
              { name: 'AI management', url: `${PagePath.aiManagementBase}` },
              { name: 'Agents', url: `${PagePath.aiManagementBase}${PagePath.aiManagementAgents.replace(':companySlug', companyVariables.slug!)}` },
              { name: 'Agent', url: location.pathname },
            ]}
            title="Agent"
            subtitle="Customize agent prompts and behavior"
          />
        )}

        {!!state.agent && (
          <form
            id="save-agent"
            onSubmit={handleSubmit(async () => {
              if (!state.agent || !companyVariables.slug) return;

              actions.startLoader({ path: PagePath.aiManagementAgent, type: FetchType.Sending });

              await actions.upsertAgent({
                companySlug: companyVariables.slug,
                agentTypeName: state.agent.agentTypeName,
                agent: state.agent,
              });

              actions.stopLoader(PagePath.aiManagementAgent);
            })}
            style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 24 }}
          >
            <Tabs static>
              <Tab active={state.selectedTab === store.TabIds.Basic} id={store.TabIds.Basic} name={store.TabIds.Basic} onClick={() => dispatch({ type: store.AgentPageActionTypes.SetSelectedTab, payload: store.TabIds.Basic })} />
              <Tab active={state.selectedTab === store.TabIds.Advanced} id={store.TabIds.Advanced} name={store.TabIds.Advanced} onClick={() => dispatch({ type: store.AgentPageActionTypes.SetSelectedTab, payload: store.TabIds.Advanced })} />
            </Tabs>

            {state.selectedTab === store.TabIds.Basic && (
              <React.Fragment>
                <WidgetCard title="System Prompt" subtitle="The system prompt provides instructions to the AI model about its role and how it should respond">
                  <TextArea
                    label="System Prompt Override"
                    value={state.agent.systemPromptOverride}
                    onChange={e =>
                      dispatch({
                        type: store.AgentPageActionTypes.UpdateSystemPrompt,
                        payload: e.target.value,
                      })
                    }
                    responsive
                  />
                </WidgetCard>

                <WidgetCard title="User Input Framework" subtitle="Define the structure that wraps around user inputs. The intro appears before user inputs, and the outro appears after them.">
                  <TextArea
                    label="Intro (Before User Input)"
                    value={state.agent.userPromptTemplateOverride.intro}
                    onChange={e =>
                      dispatch({
                        type: store.AgentPageActionTypes.UpdateUserPromptIntro,
                        payload: e.target.value,
                      })
                    }
                    responsive
                  />

                  <TextArea
                    label="Outro (After User Input)"
                    value={state.agent.userPromptTemplateOverride.outro}
                    onChange={e =>
                      dispatch({
                        type: store.AgentPageActionTypes.UpdateUserPromptOutro,
                        payload: e.target.value,
                      })
                    }
                    responsive
                  />
                </WidgetCard>

                {/* TODO: review once Contexts is working */}
                <WidgetCard title="Contexts" subtitle="Add relevant contexts to provide the AI with additional information it can reference when generating responses">
                  <div style={{ display: 'flex', flexDirection: 'row', gap: 12, alignItems: 'center' }}>
                    <PillButtonGroup
                      backgroundColour="borders_2"
                      activeTextColour="text"
                      buttons={[
                        {
                          id: store.PillButtonIds.SearchContexts,
                          label: PillButtonDescriptions.SearchContexts,
                          active: state.selectedPillButton === store.PillButtonIds.SearchContexts,
                        },
                        {
                          id: store.PillButtonIds.SelectedContexts,
                          label: `${PillButtonDescriptions.SelectedContexts} (${state.agent.contextIds.length})`,
                          active: state.selectedPillButton === store.PillButtonIds.SelectedContexts,
                        },
                      ]}
                      onClick={buttons =>
                        dispatch({
                          type: store.AgentPageActionTypes.SetSelectedPillButton,
                          payload: buttons[0].id ?? store.PillButtonIds.SearchContexts,
                        })
                      }
                    />
                  </div>

                  <SearchBar
                    loading={fetchState[PagePath.aiManagementAgent].status === FetchStatus.Active && fetchState[PagePath.aiManagementAgent].type === FetchType.PageFetching}
                    label="Search contexts"
                    onInput={event => {
                      const search = (event.target as HTMLInputElement).value;
                      dispatch({ type: store.AgentPageActionTypes.SetSearch, payload: search });

                      if (!search.trim() || !state.availableContexts?.length) {
                        dispatch({ type: store.AgentPageActionTypes.SetSearchedContexts, payload: [] });
                        return;
                      }

                      const searchedContexts = state.availableContexts.filter(availableContext => availableContext.label.toLowerCase().includes(search.toLowerCase()));
                      dispatch({ type: store.AgentPageActionTypes.SetSearchedContexts, payload: searchedContexts });
                    }}
                    responsive
                  />

                  {state.selectedPillButton === store.PillButtonIds.SearchContexts && (
                    <Table emptyState={<EmptyState title="No contexts found" subtitle={!!state.search ? 'Try a different search term' : 'Enter a search term to find contexts'} />}>
                      {state.searchedContexts
                        ?.filter(availableContext => !state.agent?.contextIds.includes(availableContext.id))
                        .map(searchedContext => (
                          <TableRow key={searchedContext.id} id={searchedContext.id}>
                            <TableColumn id="label" label="Label">
                              {searchedContext.label}
                            </TableColumn>

                            <TableColumn id="type" label="Type">
                              {searchedContext.type}
                            </TableColumn>

                            <TableColumn id="actions">
                              <TertiaryButton
                                type="button"
                                onClick={() => {
                                  if (!state.agent) return;

                                  dispatch({ type: store.AgentPageActionTypes.UpdateContextIds, payload: [...state.agent.contextIds, searchedContext.id] });
                                }}
                              >
                                Add
                              </TertiaryButton>
                            </TableColumn>
                          </TableRow>
                        ))}
                    </Table>
                  )}

                  {state.selectedPillButton === store.PillButtonIds.SelectedContexts && (
                    <Table emptyState={<EmptyState title="No contexts selected" subtitle="Search and add contexts to your agent" />}>
                      {state.availableContexts
                        ?.filter(availableContext => state.agent?.contextIds.includes(availableContext.id))
                        .map(availableContext => (
                          <TableRow key={availableContext.id} id={availableContext.id}>
                            <TableColumn id="label" label="Label">
                              {availableContext.label}
                            </TableColumn>

                            <TableColumn id="type" label="Type">
                              {availableContext.type}
                            </TableColumn>

                            <TableColumn id="actions">
                              <TertiaryButton
                                type="button"
                                onClick={() => {
                                  if (!state.agent) return;

                                  dispatch({ type: store.AgentPageActionTypes.UpdateContextIds, payload: state.agent.contextIds.filter(contextId => contextId !== availableContext.id) });
                                }}
                              >
                                Remove
                              </TertiaryButton>
                            </TableColumn>
                          </TableRow>
                        ))}
                    </Table>
                  )}
                </WidgetCard>
              </React.Fragment>
            )}

            {state.selectedTab === store.TabIds.Advanced && (
              <React.Fragment>
                <WidgetCard title="Input field labels" subtitle="Customize the labels shown to users in the input form. These help users understand what information to provide in each field.">
                  {state.agent.propertyLabelOverrides.length > 0 ? (
                    <React.Fragment>
                      {state.agent.propertyLabelOverrides.map((override, index) => (
                        <TextField
                          key={override.propertyName}
                          type="text"
                          label={override.propertyName}
                          value={override.label}
                          onChange={event => {
                            const nextOverrides = [...state.agent!.propertyLabelOverrides];
                            nextOverrides[index] = {
                              ...nextOverrides[index],
                              label: event.target.value,
                            };
                            dispatch({
                              type: store.AgentPageActionTypes.SetAgent,
                              payload: {
                                ...state.agent!,
                                propertyLabelOverrides: nextOverrides,
                              },
                            });
                          }}
                          responsive
                        />
                      ))}
                    </React.Fragment>
                  ) : (
                    <EmptyState title="No input field labels" subtitle="This agent has no input field labels to customize" />
                  )}
                </WidgetCard>

                <WidgetCard title="Output field descriptions" subtitle="Define how the AI should format its output fields. These descriptions guide the AI on what to include in each field of the generated JSON.">
                  {state.agent.propertyDescriptionOverrides.length > 0 ? (
                    <React.Fragment>
                      {state.agent.propertyDescriptionOverrides.map((override, index) => (
                        <TextField
                          key={override.propertyName}
                          type="text"
                          label={override.propertyName}
                          value={override.description || ''}
                          onChange={event => {
                            const nextOverrides = [...state.agent!.propertyDescriptionOverrides];
                            nextOverrides[index] = {
                              ...nextOverrides[index],
                              description: event.target.value,
                            };
                            dispatch({
                              type: store.AgentPageActionTypes.SetAgent,
                              payload: {
                                ...state.agent!,
                                propertyDescriptionOverrides: nextOverrides,
                              },
                            });
                          }}
                          responsive
                        />
                      ))}
                    </React.Fragment>
                  ) : (
                    <EmptyState title="No output field descriptions" subtitle="This agent has no output field descriptions to customize" />
                  )}
                </WidgetCard>
              </React.Fragment>
            )}

            <div className="buttonGroup" style={{ display: 'flex', gap: '12px', justifyContent: 'space-between' }}>
              <TertiaryButton type="button" onClick={() => dispatch({ type: store.AgentPageActionTypes.SetShowConfirmationModal, payload: true })}>
                Reset to defaults
              </TertiaryButton>

              <Button type="button" filled>
                Update Agent
              </Button>
            </div>
          </form>
        )}
      </div>

      <Modal open={state.showConfirmationModal} onClose={() => dispatch({ type: store.AgentPageActionTypes.SetShowConfirmationModal, payload: false })}>
        <ConfirmationModalLayout
          title="Reset Agent"
          subtitle="Are you sure you want to undo your customization?"
          onClickCancel={() => dispatch({ type: store.AgentPageActionTypes.SetShowConfirmationModal, payload: false })}
          submitButtonText="Reset"
          onClickSubmit={async () => {
            if (!companyVariables.slug || !agentTypeName) return;

            actions.startLoader({ path: PagePath.aiManagementAgent, type: FetchType.Sending });
            dispatch({ type: store.AgentPageActionTypes.SetShowConfirmationModal, payload: false });

            await actions.deleteAgent({ companySlug: companyVariables.slug, agentTypeName });

            const agent = await actions.getAgent({ companySlug: companyVariables.slug, agentTypeName });
            dispatch({ type: store.AgentPageActionTypes.SetAgent, payload: agent });

            actions.stopLoader(PagePath.aiManagementAgent);
          }}
        />
      </Modal>

      {!!state.agent && !!companyVariables.slug && (
        <CascadingPanels openPanelIds={openPanelIds} onClosePanel={previous}>
          <CascadingPanel id={CascadingPanelIds.Request}>
            <TestAgentPanel
              agent={state.agent}
              companySlug={companyVariables.slug}
              onExecute={response => {
                dispatch({ type: store.AgentPageActionTypes.SetResponse, payload: response });
                next(CascadingPanelIds.Response);
              }}
            />
          </CascadingPanel>

          <CascadingPanel id={CascadingPanelIds.Response}>
            <div className="panelContent">
              <PanelHeader title={`${state.agent.displayName || 'Agent'} Response`} divider />
              <JsonViewer data={state.response} />
            </div>
          </CascadingPanel>
        </CascadingPanels>
      )}
    </PageLoaderLayer>
  );
}
